import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarDays, CheckCircle2, XCircle, Wallet, Package, Tag, Percent, CreditCard, Coins, Bell, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const statCards = [
  { key: "total_appointments", label: "Citas totales", icon: CalendarDays },
  { key: "confirmed", label: "Confirmadas", icon: CheckCircle2 },
  { key: "completed", label: "Completadas", icon: CheckCircle2 },
  { key: "cancelled", label: "Canceladas", icon: XCircle },
  { key: "services_count", label: "Servicios", icon: Tag },
  { key: "products_count", label: "Productos", icon: Package },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [credits, setCredits] = useState({ total: 0, count: 0, items: [] });
  const [reminders, setReminders] = useState(null);
  const [remindersLoading, setRemindersLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/staff-commissions").then((r) => setCommissions(r.data || [])).catch(() => {});
    api.get("/admin/credits").then((r) => setCredits(r.data || { total: 0, count: 0, items: [] })).catch(() => {});
    api.get("/admin/reminders/status").then((r) => setReminders(r.data)).catch(() => {});
  }, []);

  async function markUsed(id) {
    try {
      await api.post(`/admin/credits/${id}/mark-used`, { note: "Aplicado por admin" });
      const { data } = await api.get("/admin/credits");
      setCredits(data);
    } catch {}
  }

  async function runReminders() {
    setRemindersLoading(true);
    try {
      const { data } = await api.post("/admin/reminders/run");
      const { data: newStatus } = await api.get("/admin/reminders/status");
      setReminders(newStatus);
      const { toast } = await import("sonner");
      toast.success(data.sent > 0 ? `${data.sent} recordatorio(s) enviado(s)` : "No hay recordatorios pendientes");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("Error al ejecutar el job");
    } finally { setRemindersLoading(false); }
  }

  return (
    <div>
      <div className="mb-10">
        <p className="overline">Resumen</p>
        <h1 className="font-display text-4xl font-light tracking-tight mt-3">Tu negocio hoy</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-stats-grid">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.key} className="border-slate-200 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="overline">{s.label}</p>
                  <Icon className="h-4 w-4 text-slate-400" />
                </div>
                <p className="font-display text-4xl font-light mt-4">{stats?.[s.key] ?? "—"}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card className="border-slate-200 card-hover sm:col-span-2 lg:col-span-3 text-slate-50" style={{ backgroundColor: "#1E3A8A" }} data-testid="admin-revenue-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-slate-300">Ingresos totales</p>
                <p className="font-display text-5xl font-light mt-2" data-testid="admin-revenue-total">${(stats?.revenue_total ?? 0).toFixed(2)}</p>
              </div>
              <Wallet className="h-8 w-8 text-slate-300" />
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-4 pt-5 border-t border-white/15">
              <div data-testid="admin-revenue-appointments">
                <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Citas completadas
                </div>
                <p className="font-display text-2xl font-light mt-2">${(stats?.appointments_revenue ?? 0).toFixed(2)}</p>
                <p className="text-slate-400 text-xs mt-1">{stats?.completed ?? 0} cita(s) cerradas</p>
              </div>
              <div data-testid="admin-revenue-deposits">
                <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                  <CreditCard className="h-3.5 w-3.5" /> Anticipos Stripe
                </div>
                <p className="font-display text-2xl font-light mt-2">${(stats?.deposits_revenue ?? 0).toFixed(2)}</p>
                <p className="text-slate-400 text-xs mt-1">{stats?.deposits_count ?? 0} anticipo(s) abiertos</p>
              </div>
              <div data-testid="admin-revenue-sales">
                <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                  <Package className="h-3.5 w-3.5" /> Ventas POS
                </div>
                <p className="font-display text-2xl font-light mt-2">${(stats?.sales_revenue ?? 0).toFixed(2)}</p>
                <p className="text-slate-400 text-xs mt-1">{stats?.sales_count ?? 0} venta(s) registradas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {reminders && (
        <Card className="border-slate-200 mt-6" data-testid="admin-reminders-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#1E3A8A]" />
                <p className="overline">Recordatorios 24h</p>
                {reminders.scheduler_running ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] border border-emerald-200 font-medium" data-testid="reminders-status-active">● Activo</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] border border-red-200 font-medium" data-testid="reminders-status-inactive">● Detenido</span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={runReminders} disabled={remindersLoading} data-testid="reminders-run-btn">
                <Send className="h-3 w-3 mr-1" /> {remindersLoading ? "Ejecutando…" : "Ejecutar ahora"}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Se envía un correo automáticamente <strong>24h antes</strong> de cada cita confirmada. El scan corre cada {reminders.scan_interval_min} min.</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[10px] tracking-wider uppercase text-slate-500">Mañana ({reminders.tomorrow_date})</p>
                <p className="font-display text-2xl font-light mt-1" data-testid="reminders-tomorrow-total">{reminders.total_tomorrow}</p>
                <p className="text-xs text-slate-500">cita(s) confirmadas</p>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-[10px] tracking-wider uppercase text-amber-700">Pendientes</p>
                <p className="font-display text-2xl font-light mt-1 text-amber-800" data-testid="reminders-pending">{reminders.pending_tomorrow}</p>
                <p className="text-xs text-amber-700">por notificar</p>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-[10px] tracking-wider uppercase text-emerald-700">Enviados 24h</p>
                <p className="font-display text-2xl font-light mt-1 text-emerald-800" data-testid="reminders-sent-24h">{reminders.sent_last_24h}</p>
                <p className="text-xs text-emerald-700">recordatorios</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {credits.count > 0 && (
        <Card className="border-slate-200 mt-6" data-testid="admin-credits-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-[#1E3A8A]" />
                <p className="overline">Saldos a favor de clientes</p>
              </div>
              <p className="font-display text-xl text-[#1E3A8A]">${credits.total.toFixed(2)} MXN</p>
            </div>
            <p className="text-xs text-slate-500 mb-4">Anticipos de citas canceladas que puedes aplicar cuando el cliente regrese. Al finalizar la nueva cita, marca el saldo como usado.</p>
            <div className="space-y-2">
              {credits.items.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`credit-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{c.client_email}</p>
                    <p className="text-xs text-slate-500">De: {c.source_service_name || "—"} · {c.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <p className="font-display text-lg text-[#1E3A8A]">${c.amount.toFixed(2)}</p>
                    <Button size="sm" variant="outline" onClick={() => markUsed(c.id)} data-testid={`credit-mark-used-${c.id}`}>Marcar usado</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {commissions.length > 0 && (
        <Card className="border-slate-200 mt-6" data-testid="commissions-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Percent className="h-4 w-4 text-[#1E3A8A]" />
              <p className="overline">Comisiones acumuladas (histórico)</p>
            </div>
            <p className="text-xs text-slate-500 mb-4">Suma de comisiones registradas al completar citas. Las comisiones se congelan al cerrar cada cita — cambios posteriores no las afectan.</p>
            <div className="space-y-2">
              {commissions.map((c) => (
                <div key={c.staff_id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`commission-staff-${c.staff_id}`}>
                  <div>
                    <p className="font-medium text-sm">{c.staff_name || "—"}</p>
                    <p className="text-xs text-slate-500">{c.appointments_count} cita(s) completadas</p>
                  </div>
                  <p className="font-display text-xl font-light text-[#1E3A8A]">${c.total_amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
